import React, { useEffect, useId, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { MinusIcon, PlusIcon, RotateCcwIcon } from "lucide-react";
import CloseIconNew from "../icons/CloseIconNew";
import style from "./DocumentPreviewModal.module.scss";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.25;

const DocumentPreviewModal = ({
  isOpen,
  imageUrl,
  title,
  closeLabel,
  onClose,
}) => {
  const titleId = useId();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [imageUrl, isOpen]);

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (event) => {
      const { x, y, panX, panY } = dragStartRef.current;

      setPan({
        x: panX + event.clientX - x,
        y: panY + event.clientY - y,
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleZoomOut = () => {
    setZoom((currentZoom) => {
      const nextZoom = Math.max(MIN_ZOOM, currentZoom - ZOOM_STEP);
      if (nextZoom === 1) setPan({ x: 0, y: 0 });
      return nextZoom;
    });
  };

  const handleZoomIn = () => {
    setZoom((currentZoom) => Math.min(MAX_ZOOM, currentZoom + ZOOM_STEP));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
  };

  const handlePreviewMouseDown = (event) => {
    if (event.button !== 0 || zoom <= 1) return;

    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setIsDragging(true);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className={style.overlay}
      onClick={onClose}
      data-portal-modal="true"
    >
      <div
        className={style.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={style.header}>
          <h2 id={titleId} className={style.title}>
            {title}
          </h2>
          <div className={style.headerActions}>
            <div className={style.zoomControls} aria-label="Масштаб документа">
              <button
                type="button"
                className={style.zoomButton}
                onClick={handleZoomOut}
                aria-label="Уменьшить"
                disabled={zoom === MIN_ZOOM}
              >
                <MinusIcon aria-hidden="true" size={18} strokeWidth={2} />
              </button>
              <span className={style.zoomValue}>{Math.round(zoom * 100)}%</span>
              {zoom !== 1 && (
                <button
                  type="button"
                  className={style.zoomButton}
                  onClick={handleResetZoom}
                  aria-label="Сбросить масштаб"
                  title="Сбросить масштаб"
                >
                  <RotateCcwIcon aria-hidden="true" size={18} strokeWidth={2} />
                </button>
              )}
              <button
                type="button"
                className={style.zoomButton}
                onClick={handleZoomIn}
                aria-label="Увеличить"
                disabled={zoom >= MAX_ZOOM}
              >
                <PlusIcon aria-hidden="true" size={18} strokeWidth={2} />
              </button>
            </div>
            <button
              type="button"
              className={style.close_btn}
              onClick={onClose}
              aria-label={closeLabel}
            >
              <CloseIconNew width="24" height="24" fill="#0D3B66" />
            </button>
          </div>
        </div>
        <div
          className={style.content}
          data-testid="document-preview-area"
          onMouseDown={handlePreviewMouseDown}
          style={{ cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
        >
          <img
            className={style.image}
            src={imageUrl}
            alt={title}
            draggable={false}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DocumentPreviewModal;
